import { ReactElement, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApplicationLocationContext } from "../../utils/ApplicationState";
import { config, pageNames } from "../../authConfig";
import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid2, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import CreditCard from "@mui/icons-material/CreditCard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PinDropIcon from "@mui/icons-material/PinDrop";
import AddIcon from "@mui/icons-material/Add";
import { Business, LocationOn } from "@mui/icons-material";
import LabeledTextField from "./components/LabeledTextField";
import { VendorSelectionDialog } from "./components/VendorSelectionDialog";
import Vendor from "./interfaces/Vendor";
import SNEvent from "./interfaces/SNEvent";
import { StateEnum } from "./HorizontalLinearStepper";
import dayjs from "dayjs";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import { useNavigate } from "react-router-dom";
import style from "./styles/CreateEvent.module.css";
import { LoadingIndicator } from "../../Common/UI/components/Loading";
import { LocationType } from "./interfaces/Location";
import { theme } from "../../Common/UI/styles/theme";
import { useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import { useSpiritNightTransition } from "../../home/ui-components/SpiritNightTransition";
import useIsAdminAndViewOnly from "../../Common/UI/hooks/useIsAdminAndViewOnly";

export const EventCreate = (): ReactElement => {
  const { isViewOnly } = useIsAdminAndViewOnly();
  const { canCreateEvents } = useSpiritNightTransition();
  const { instance } = useMsal();
  const { setState } = useContext(ApplicationLocationContext);
  const abortController = useRef<AbortController | null>(null);
  const execute = useCallback(useFetchMsal(), []);
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [locations, setLocations] = useState<LocationType[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [taxIdIsValid, setTaxIdIsValid] = useState(false);
  const [checkboxPaymentAddress, setCheckboxPaymentAddress] = useState<boolean>(false);
  const [checkboxLegalAddress, setCheckboxLegalAddress] = useState<boolean>(false);
  const [showVendorSelectDialog, setShowVendorSelectDialog] = useState<boolean>(false);

  const [event, setEvent] = useState<SNEvent>({
    id: undefined,
    date: undefined,
    vendor: undefined,
    location: undefined,
    locationName: "",
    managingPartnerName: "",
    managingPartnerEmail: "",
    requestorEmail: "",
    requestorName: "",
    state: "New",
    stateId: StateEnum.New,
    comment: undefined,
  });
  const [isCreating, setIsCreating] = useState<boolean>(false);

  useEffect(() => {
    if (canCreateEvents) locationsCall();
  }, [canCreateEvents]);

  useEffect(() => {
    const activeAccount: null | AccountInfo = instance.getActiveAccount();
    if (activeAccount?.name) {
      event.requestorName = activeAccount?.name;
      event.requestorEmail = activeAccount?.username;
      setEvent(event);
    }
  }, []);

  const locationsCall = function () {
    setLocationsLoading(true);
    execute("GET", `${config.rootAPIUrl}${config.rootAPIList?.locations}`)
      .then((result) => {
        if (!result || !result.locations || result.locations.length == 0) {
          throw new Error("No Locations Found.");
        }
        setLocations(result.locations);
        if (result.locations.length == 1) {
          setSelectedLocation(result.locations[0].locationNumber);
        }
      })
      .catch((error) => {
        throw new Error("Error While Pulling Locations.");
      })
      .finally(() => {
        setLocationsLoading(false);
      });
  };

  useEffect(() => {
    setState({ path: [pageNames.events, pageNames.eventCreate] });
  }, [setState]);

  useEffect(() => {
    const locationTypeObj: LocationType | undefined = locations.find((location: LocationType) => location.locationNumber === selectedLocation);

    const updatedEvent = {
      ...event,
      location: selectedLocation,
      locationName: locationTypeObj?.locationName,
      managingPartnerName: locationTypeObj?.fullName,
      managingPartnerEmail: locationTypeObj?.email,
    };

    setEvent(updatedEvent);
  }, [selectedLocation]);

  const handleCreateClick = async () => {
    if (!canCreateEvents) return;
    try {
      abortController.current?.abort();
      abortController.current = new AbortController();

      setIsCreating(true);
      const url = `${config.rootAPIUrl}${config.rootAPIList?.eventCreate}`;
      const response = await execute("POST", url, event, abortController.current.signal);
      setEvent(response);
      setIsCreating(false);
      navigate("../events/" + response.id);
    } catch (error) {
      setIsCreating(false);
      console.error(error);
      throw new Error("Event Creation Failed!");
    }
  };

  const handleClose = () => setShowVendorSelectDialog(false);

  const handleVendorSelected = function (vendor: Vendor) {
    event.vendor = vendor;
    setEvent(event);
  };

  const handleLegalAddressCheckbox = function () {
    setCheckboxLegalAddress(!checkboxLegalAddress);
  };

  const handlePaymentAddressCheckbox = function () {
    setCheckboxPaymentAddress(!checkboxPaymentAddress);
  };

  if (!canCreateEvents) {
    return <Box role="status" sx={{ px: 3, py: 2 }}>New event creation is unavailable in this application. Please see the Force4Good notice above and contact your FMM with any questions.</Box>;
  }

  return (
    <Box sx={{ paddingX: "24px" }}>
      <LoadingIndicator show={isCreating}>
        <div id={style.border}>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            marginBottom={"32px"}
          >
            <Typography component={"h5"} variant="h5">
              Create a Spirit Night Event
            </Typography>
            {!isViewOnly && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disableElevation
                onClick={handleCreateClick}
                disabled={!canCreateEvents || isCreating || !event.vendor || event.date == undefined || checkboxLegalAddress === false || checkboxPaymentAddress === false || selectedLocation === ""}
              >
                Create
              </Button>
            )}
          </Box>

          <Grid2 container alignItems={"flex-start"} columnSpacing={8}>
            <Grid2 container size={6} spacing={2}>
              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={PinDropIcon} label="Location" />
              </Grid2>
              <Grid2 size={7}>
                <LoadingIndicator show={locationsLoading} small={true}>
                  <FormControl sx={{ width: "100%" }}>
                    <Select
                      labelId="report-label"
                      id="report-type"
                      value={selectedLocation}
                      onChange={(ev: SelectChangeEvent) => {
                        setSelectedLocation(ev.target.value as string);
                      }}
                      disabled={locations.length == 1}
                    >
                      {locations.map((x) => (
                        <MenuItem key={x.locationNumber + " " + x.employeeNumber} value={x.locationNumber}>
                          {x.locationName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </LoadingIndicator>
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={BadgeIcon} label="Managing Partner Name" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event.managingPartnerName || ""} disabled required />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={EmailIcon} label="Managing Partner Email" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event.managingPartnerEmail || ""} disabled required />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={BadgeIcon} label="Requestor Name" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event.requestorName || ""} disabled required />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={EmailIcon} label="Requestor Email" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event.requestorEmail || ""} disabled required />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={CalendarMonthIcon} label="Event Date" />
              </Grid2>
              <Grid2 size={7}>
                <DatePicker
                  sx={{
                    width: "100%",
                    border: !event.date ? "2px solid red" : "1px solid #ccc",
                    borderRadius: "13px",
                    "& .MuiInputBase-root": {
                      backgroundColor: !event.date ? "#ffe6e6" : "unset",
                    },
                  }}
                  value={event.date ? dayjs(event.date) : null}
                  onChange={(newValue) => {
                    const updatedEvent = {
                      ...event,
                      date: newValue?.format("YYYY-MM-DD"),
                    };
                    setEvent(updatedEvent);
                  }}
                />
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={Business} label="Vendor" />
              </Grid2>
              <Grid2 size={7}>
                <Button id={style.selectVendor} variant="contained" disableElevation onClick={() => setShowVendorSelectDialog(true)}>
                  Select
                </Button>
              </Grid2>

              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={PersonIcon} label="Vendor Name" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event.vendor?.companyName || ""} disabled required />
              </Grid2>
            </Grid2>

            <Grid2 container size={6} spacing={2}>
              <Grid2 size={5} alignContent={"center"}>
                <LabeledTextField LabelIcon={RequestQuoteRoundedIcon} label="Vendor Tax ID" />
              </Grid2>
              <Grid2 size={7}>
                <TextField
                  sx={{ width: "100%" }}
                  value={event.vendor?.taxNumber || ""}
                  disabled
                  required
                  onChange={() => {
                    event.vendor?.taxNumber ? setTaxIdIsValid(true) : setTaxIdIsValid(false);
                  }}
                />
              </Grid2>

              <Grid2 size={5} sx={{ marginTop: "8px" }}>
                <LabeledTextField LabelIcon={LocationOn} label="Vendor Legal Address" />
              </Grid2>
              <Grid2 size={7}>
                <TextField sx={{ width: "100%" }} value={event.vendor?.legalAddress || ""} multiline rows={5} variant="standard" className="txt-area" disabled required />
              </Grid2>

              <Grid2 size={5}></Grid2>
              <Grid2
                container
                justifyContent="flex-start"
                alignItems="flex-start"
                size={7}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  marginTop: "-15px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />}
                      sx={{ alignSelf: "flex-start", marginRight: 1 }}
                      checked={checkboxLegalAddress}
                      onClick={handleLegalAddressCheckbox}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ alignSelf: "flex-start", marginTop: "9px" }}>
                      Legal Address reviewed & Confirmed.
                    </Typography>
                  }
                />
              </Grid2>

              <Grid2 size={5} sx={{ marginTop: "8px" }}>
                <LabeledTextField LabelIcon={CreditCard} label="Vendor Payment Address" />
              </Grid2>
              <Grid2 size={7}>
                <TextField multiline rows={5} variant="standard" className="txt-area" sx={{ width: "100%" }} value={event.vendor?.paymentAddress || ""} disabled required />
              </Grid2>

              <Grid2 size={5}></Grid2>
              <Grid2
                container
                justifyContent="flex-start"
                alignItems="flex-start"
                size={7}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  marginTop: "-15px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />}
                      sx={{ alignSelf: "flex-start", marginRight: 1 }}
                      checked={checkboxPaymentAddress}
                      onClick={handlePaymentAddressCheckbox}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ alignSelf: "flex-start", marginTop: "9px" }}>
                      Payment Address Reviewed & Confirmed.
                    </Typography>
                  }
                />
              </Grid2>
            </Grid2>
          </Grid2>
          {showVendorSelectDialog ? <VendorSelectionDialog {...{ handleClose, handleVendorSelected }} /> : <></>}
        </div>
      </LoadingIndicator>
    </Box>
  );
};
